import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { useToast } from '@/components/Toast';
import { specOf } from '@/domain/catalog';
import { assess } from '@/domain/engine';
import { asNonNegative } from '@/domain/values';
import {
  formatCompact,
  formatCurrency,
  formatDate,
  formatMass,
  formatMinutes,
  formatNumber,
  formatPercent,
  formatRatio,
} from '@/lib/format';
import type { MissingRef } from '@/domain/types';
import { useProjectStore } from '@/app/projectStore';

/** 一行「标签 / 值 / 单位」，报告与打印共用 */
function Row({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line/60 py-1.5">
      <span className="text-xs text-fg-muted">{label}</span>
      <span className="tabular text-xs font-medium text-fg">
        {value}
        {unit ? <span className="ml-1 text-[11px] font-normal text-fg-subtle">{unit}</span> : null}
      </span>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="print-keep rounded-lg border border-line bg-surface-1 px-4 py-3.5">
      <header className="mb-2.5">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-[11px] text-fg-muted">{subtitle}</p> : null}
      </header>
      {children}
    </section>
  );
}

/** 评估报告：屏幕上可读，打印 / 导出 PDF 时自动切成 A4 版式 */
export function ReportPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { getProject, ready } = useProjectStore();
  const project = getProject(projectId);
  const assessment = useMemo(() => (project ? assess(project) : null), [project]);

  if (!ready) return null;

  if (!project || !assessment) {
    return (
      <div className="rounded-lg border border-line bg-surface-1 px-4 py-6 text-center">
        <p className="text-sm text-fg">找不到这个项目。</p>
        <Button className="mt-3" size="lg" variant="primary" onClick={() => navigate('/')}>
          返回项目列表
        </Button>
      </div>
    );
  }

  const { mass, thrust, twr, hover, power, endurance, cost, margins } = assessment;
  const massTotal = formatMass(mass.value?.totalG ?? null);

  const missingByComponent = new Map<string, MissingRef[]>();
  for (const ref of assessment.missingAll) {
    const list = missingByComponent.get(ref.componentLabel) ?? [];
    list.push(ref);
    missingByComponent.set(ref.componentLabel, list);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="no-print flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => navigate(`/p/${project.id}`)}
          className="text-xs text-fg-muted transition-colors hover:text-fg"
        >
          ← 返回项目
        </button>
        <div className="flex gap-2">
          <Button size="lg" variant="default" onClick={() => navigate(`/p/${project.id}`)}>
            继续编辑
          </Button>
          <Button
            size="lg"
            variant="primary"
            onClick={() => {
              toast.info('已打开打印面板', '在目标位置选择「另存为 PDF」即可导出报告。');
              window.setTimeout(() => window.print(), 150);
            }}
          >
            打印 / 导出 PDF
          </Button>
        </div>
      </div>

      <div className="print-page flex flex-col gap-3">
        <Section title="无人机搭建评估报告" subtitle={`${project.name} · 生成于 ${formatDate(new Date().toISOString())}`}>
          <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4">
            <Row label="机型" value="四轴" />
            <Row label="安全系数预留" value={formatPercent(assessment.reservePct, 0)} />
            <Row label="预估起飞质量" value={massTotal.value} unit={massTotal.unit} />
            <Row label="预估总推力" value={formatCompact(thrust.value?.totalG ?? null)} unit="g" />
            <Row label="裸推重比" value={formatRatio(twr.value?.bare ?? null)} />
            <Row label="有效推重比" value={formatRatio(twr.value?.effective ?? null)} />
            <Row label="预估金额" value={formatCurrency(cost.value?.total ?? null)} />
            <Row label="数据完整度" value={formatPercent(assessment.completeness * 100, 0)} />
          </div>
          <p className="mt-2.5 text-xs leading-relaxed text-fg">
            结论：
            <Badge tone={assessment.verdict.tone} solid={assessment.verdict.level !== 'unknown'}>
              {assessment.verdict.label}
            </Badge>
            <span className="ml-2 text-fg-muted">{assessment.verdict.description}</span>
          </p>
        </Section>

        <Section title="部件参数" subtitle="空值表示用户未提供该参数，未计入计算">
          <div className="flex flex-col gap-3">
            {project.components.map((component) => {
              const spec = specOf(component.kind);
              const entries = spec.fields.map((field) => {
                const value = component.fields[field.key];
                const filled = value !== null && value !== undefined && `${value}`.trim() !== '';
                const numeric = filled ? asNonNegative(value) : null;
                return (
                  <Row
                    key={field.key}
                    label={field.label}
                    value={!filled ? '未填' : numeric !== null ? formatCompact(numeric) : `${value}`}
                    unit={filled && numeric !== null ? field.unit : undefined}
                  />
                );
              });
              return (
                <div key={component.id} className="print-keep">
                  <div className="mb-1 flex items-center gap-2">
                    <h3 className="text-xs font-semibold text-fg">
                      {component.label?.trim() || spec.label}
                    </h3>
                    <Badge>×{component.qty}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 md:grid-cols-3">{entries}</div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="评估结果">
          <div className="grid grid-cols-2 gap-x-4 md:grid-cols-3">
            <Row label="单电机推力" value={formatCompact(thrust.value?.perMotorG ?? null)} unit="g" />
            <Row label="电机数量" value={formatNumber(thrust.value?.motorCount ?? null, 0)} unit="个" />
            <Row label="有效推力" value={formatCompact(twr.value?.usableThrustG ?? null)} unit="g" />
            <Row label="空载质量" value={formatMass(mass.value?.emptyG ?? null).value} unit={formatMass(mass.value?.emptyG ?? null).unit} />
            <Row label="载荷质量" value={formatMass(mass.value?.payloadG ?? null).value} unit={formatMass(mass.value?.payloadG ?? null).unit} />
            <Row label="悬停油门" value={formatPercent(hover.value?.throttlePct ?? null, 0)} />
            <Row label="标称电压" value={formatNumber(power.value?.voltageV ?? null, 1)} unit="V" />
            <Row label="满油门总电流" value={formatNumber(power.value?.maxCurrentA ?? null, 1)} unit="A" />
            <Row label="悬停电流" value={formatNumber(power.value?.hoverCurrentA ?? null, 1)} unit="A" />
            <Row label="预估续航" value={formatMinutes(endurance.value?.minutes ?? null)} />
            <Row label="每 100 g 成本" value={formatCurrency(cost.value?.per100g ?? null)} />
            <Row label="推力来源" value={
              thrust.value
                ? thrust.value.source === 'input'
                  ? '实测值'
                  : thrust.value.source === 'estimated'
                    ? '经验估算'
                    : thrust.value.source === 'mixed'
                      ? '实测 + 估算'
                      : '无数据'
                : '无数据'
            } />
          </div>
        </Section>

        <Section title="裕度校验">
          <ul className="flex flex-col gap-1.5">
            {margins.map((check) => (
              <li key={check.id} className="text-xs leading-relaxed text-fg-muted">
                <span className="font-medium text-fg">{check.label}：</span>
                <span className="tabular">{check.detail}</span>
              </li>
            ))}
          </ul>
        </Section>

        {missingByComponent.size > 0 ? (
          <Section title="缺失参数" subtitle="下列参数没有填写，相关指标未把它们计入">
            <ul className="flex flex-col gap-1.5">
              {[...missingByComponent.entries()].map(([component, refs]) => (
                <li key={component} className="text-xs leading-relaxed text-fg-muted">
                  <span className="font-medium text-fg">{component}：</span>
                  {refs.map((ref) => ref.fieldLabel).join('、')}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        <Section title="计算口径与免责声明">
          <ul className="flex flex-col gap-1.5 text-[11px] leading-relaxed text-fg-muted">
            <li>· 总推力 = Σ（电机最大推力 × 数量）；起飞质量 = Σ（部件重量 × 数量）。</li>
            <li>· 有效推力 = 总推力 ×（1 − 安全系数预留）；推重比、悬停油门与结论均以有效推重比为准。</li>
            <li>· 悬停油门按「推力与油门平方成正比」估算：√(1 ÷ 推重比)。</li>
            <li>· 无实测推力时按经验式估算：KV × 标称电压(电芯数 × 3.7 V) × 0.85 得转速，再按 C_T × ρ × 转速² × 桨径³ × 螺距 换算静推力，误差约 ±20%。</li>
            <li>· 续航按电池可用放电 80% 与悬停电流估算，实际受飞行方式、电池状态与温度影响。</li>
            <li>· 本报告为搭建前的快速可行性参考，不替代实测拉力台数据与适航验证。</li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
